import { action, computed, observable, reaction, runInAction } from "mobx";
import { RootStore } from "./rootStore";
import {
  ITeacherCategoryFormValues,
  ITeacherFormValues,
  ITeacherUser,
  TeacherFilter,
} from "../models/user";
import agent from "../api/agent";
import { toast } from "react-toastify";
import { history } from "../..";
import { SyntheticEvent } from "react";

const LIMIT = 10;

export default class TeacherStore {
  rootStore: RootStore;
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    reaction(
      () => this.predicate.keys(),
      () => {
        this.page = 0;
        this.loadTeachers();
      }
    );
  }

  @observable teacherUser: ITeacherUser | null = null;
  @observable submitting = false;
  @observable loadingInitial = false;
  @observable target = "";
  @observable teacherRegistry = new Map();
  @observable teacherCategoryRegistry = new Map();
  @observable teacherCount = 0;
  @observable page = 1;
  @observable predicate = new Map();
  @observable teacherFilter = new TeacherFilter();

  @action setPredicate = () => {
    this.predicate.clear();
    if (this.teacherFilter.name) {
      this.predicate.set("name", this.teacherFilter!.name);
    }
  };

  @computed get axiosParams() {
    const params = new URLSearchParams();
    params.append("limit", String(LIMIT));
    params.append("offset", `${this.page > 1 ? (this.page - 1) * LIMIT : 0}`);
    this.predicate.forEach((value, key) => {
      params.append(key, value);
    });

    return params;
  }

  @computed get totalPages() {
    return Math.ceil(this.teacherCount / LIMIT);
  }

  @action setPage = (page: number) => {
    this.page = page;
  };

  @computed get teachersByName() {
    const teachers = Array.from(this.teacherRegistry.values());
    return teachers;
  }

  @computed get teacherCategoryByCatName() {
    const teacherCategories = Array.from(this.teacherCategoryRegistry.values());
    return teacherCategories;
  }

  @action loadTeachers = async () => {
    this.loadingInitial = true;
    try {
      this.teacherRegistry.clear();
      const teachersEnvelope = await agent.Teachers.list(this.axiosParams);
      const { dataCollection, dataCount } = teachersEnvelope;
      runInAction("loading teachers", () => {
        dataCollection.forEach((user) => {
          this.teacherRegistry.set(user.id, user);
        });
        this.teacherCount = dataCount;
        this.loadingInitial = false;
      });
    } catch (error) {
      runInAction("load teachers error", () => {
        this.loadingInitial = false;
      });
      toast.error("Erro ao carregar professores");
    }
  };

  @action loadTeacher = async (id: string) => {
    this.loadingInitial = true;
    try {
      var teacher = await agent.Teachers.details(id);
      runInAction("loading teacher", () => {
        this.teacherUser = teacher;
        this.loadingInitial = false;
      });
      return teacher;
    } catch (error) {
      runInAction("load teacher error", () => {
        this.loadingInitial = false;
      });
      toast.error("Erro ao carregar professor");
    }
  };

  @action loadTeacherCategories = async (id: string) => {
    this.loadingInitial = true;
    try {
      var teacherCategoriesEnvelope =
        await agent.Teachers.teacherCategoriesList(id);
      const { dataCollection } = teacherCategoriesEnvelope;
      runInAction("loading teacher categories", () => {
        dataCollection.forEach((teacherCategory) => {
          this.teacherCategoryRegistry.set(teacherCategory.id, teacherCategory);
        });
        this.loadingInitial = false;
      });
    } catch (error) {
      runInAction("load teacher categories error", () => {
        this.loadingInitial = false;
      });
      toast.error("Erro ao carregar categorias do professor");
    }
  };

  @action createTeacher = async (values: ITeacherFormValues) => {
    try {
      this.submitting = true;
      values.email = values.email.trim();
      await agent.Teachers.create(values);
      runInAction("creating teacher", () => {
        this.submitting = false;
      });
      history.push("/admin/professores/");
    } catch (error) {
      runInAction("create teacher error", () => {
        this.submitting = false;
      });
      toast.error("Erro ao criar professor");
    }
  };

  @action editTeacher = async (teacher: ITeacherUser) => {
    this.submitting = true;
    try {
      await agent.Teachers.update(teacher);
      runInAction("editing teacher", () => {
        this.teacherRegistry.set(teacher.id, teacher);
        this.teacherUser = teacher;
        this.submitting = false;
      });
      history.push("/admin/professores/");
    } catch (error) {
      runInAction("edit teacher error", () => {
        this.submitting = false;
      });
    }
  };

  @action deleteTeacher = async (
    event: SyntheticEvent<HTMLButtonElement>,
    id: string
  ) => {
    this.submitting = true;
    this.target = event.currentTarget.name;
    try {
      await agent.Teachers.delete(id);
      runInAction("deleting teacher", () => {
        this.teacherRegistry.delete(id);
        this.submitting = false;
        this.target = "";
      });
      history.push("/admin/professores");
    } catch (error) {
      runInAction("delete teacher error", () => {
        this.submitting = false;
        this.target = "";
      });
    }
  };

  @action createTeacherCategory = async (
    values: ITeacherCategoryFormValues
  ) => {
    try {
      this.submitting = true;
      await agent.Teachers.teacherCategoryCreate(values).then(() =>
        this.loadTeacherCategories(values.appUserId)
      );
      runInAction("creating teacher", () => {
        this.submitting = false;
      });
    } catch (error) {
      runInAction("create teacher error", () => {
        this.submitting = false;
      });
      toast.error("Erro ao criar professor");
    }
  };

  @action deleteTeacherCategory = async (
    event: SyntheticEvent<HTMLButtonElement>,
    id: string
  ) => {
    this.submitting = true;
    this.target = event.currentTarget.name;
    try {
      await agent.Teachers.teacherCategoryDelete(id);
      runInAction("deleting teacher category", () => {
        this.teacherCategoryRegistry.delete(id);
        this.submitting = false;
        this.target = "";
      });
    } catch (error) {
      runInAction("delete teacher category error", () => {
        this.submitting = false;
        this.target = "";
      });
    }
  };
}
